import { defineStore } from 'pinia'

import { ApplicationConfigService } from '~/libs/store-services/application-config-service'
import { ApplicationExperienceService } from '~/libs/store-services/application-experience-service'
import appNameFromRoute from '~/libs/app-name-from-route'

import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useExperimentsStore } from '~/store/experiments'
import env from '../libs/env'
import { useAppAnalyticsStore } from '~/store/app-analytics'

const defaultExperienceArgs = {
  entrypointIdentifier: null,
  moduleIdentifier: null,
  direction: null,
};

// Seeded in Nova to match. Resolved per-surface (not via allowedAppNames, which has no
// Intelligent Lending value) so a single flag can be allocated independently per surface.
export const BANK_PULL_STATUS_EXPERIMENT_NAMES = {
  embedded: 'EM - Bank Pull Status Indicator | September 2026',
  il: 'IL - Bank Pull Status Indicator | September 2026',
  marketplace: 'Bank Pull Status Indicator | September 2026',
}

export const useApplicationConfigStore = defineStore('applicationConfig', () => {
  const { $axios, $axiosWithRetry } = useNuxtApp()
  const { experienceBaseRoute, singleConfigBaseRoute, eventOptions } = useApplicationConstants()
  const route = useRoute()
  const applicationConfigService = new ApplicationConfigService($axios, $axiosWithRetry)
  const applicationExperienceService = new ApplicationExperienceService($axios, $axiosWithRetry)
  const borrowerStore = useBorrowerStore()
  const rootStore = useRootStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const experimentsStore = useExperimentsStore()
  const appAnalyticsStore = useAppAnalyticsStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
  */

  const applicationConfig = ref(null)
  const documentUploadRequestApplicationConfig = ref(null)
  const stale = ref(true)
  const preloadedPages = ref([])
  const preloadedIdentifier = ref('')
  const isBackEvent = ref(false)
  const isDriversLicenseVerificationUiTestEnabled = ref(false)
  const isBankPullStatusIndicatorEnabled = ref(false)
  const completedModuleEventPaths = ref([])
  const navigation = ref(null)
  const submitSections = ref(null)
  const resumeData = ref(null)
  const resumeUrl = ref(null)


  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */

  const appIdentifierRoute = computed(() => {
    // TODO: Update for new route structure
    return `${affiliateCustomizationStore.preapprovalOnly ? 'preapproval' : 'marketplace'}-app`
  })

  const navItems = computed(() => {
    return navigation.value ?? []
  })

  const currentModule = computed(() => {
    return applicationConfig.value?.module ?? null;
  })

  const currentModuleIdentifier = computed(() => {
    return currentModule.value?.identifier ?? null;
  })

  const borrowerApplicationType = computed(() => {
    return applicationConfig.value?.borrowerApplicationType ?? null;
  })

  const documentUploadModules = computed(() => {
    return documentUploadRequestApplicationConfig.value?.moduleList ?? []
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  */

  async function setIsBackEvent(isBack = false) {
    isBackEvent.value = isBack
  }

  function _setAppExperienceValues({ _applicationConfig, _navigation, _submitSections, _resumeData }) {
    applicationConfig.value = _applicationConfig
    submitSections.value = _submitSections
    resumeData.value = _resumeData ?? null
    navigation.value = _buildNavigation(_navigation)

    stale.value = false
  }

  function _buildNavigation(navigation) {
    if (!navigation || !navigation.length) {
      return []
    }

    return navigation.reduce((acc, curr) => {
      // build nexted navigation sections if navLabelSection is defined
      if (curr?.navSectionLabel) {
        const section = acc.find(navItem => navItem.label === curr.navSectionLabel);
        if (section) {
          section.visibleChildren.push(curr)
          section.visited = section.visited || curr.visited;
          return acc;
        }

        acc.push({
          label: curr.navSectionLabel,
          type: 'section',
          url: curr.url,
          visited: curr.visited,
          visibleChildren: [curr],
          hiddenChildren: [],
        })
        return acc
      }

      acc.push(curr)
      return acc;
    }, [])
  }

  function _buildResumeUrl() {
    const _resumeData = resumeData.value
    if (!_resumeData) {
      resumeUrl.value = null
      return resumeUrl.value
    }
    const moduleIdentifier = _resumeData?.moduleIdentifier
    if (_resumeData && !moduleIdentifier) {
      // If resume data exists but no module identifier, application requires no further actions
      resumeUrl.value = '/app-complete-landing'
      return resumeUrl.value
    }

    const baseRoute = rootStore.isEmbedded ? '/embedded/' : '/'
    // Only setup to resume experience pathways for now. Single config resume not supported at this time.
    const builtUrl = `${baseRoute}${experienceBaseRoute}/${moduleIdentifier}`
    resumeUrl.value = builtUrl
    return resumeUrl.value
  }

  /**
   * @param resumeAppStrategy {string}  default | resumeProgress | resumeCompletedProgress | resumeLoanAppForced
   * @returns {Promise<{resumeUrl: *}>}
   */
  async function getResumeApplicationData(resumeAppStrategy = 'default') {
    const borrowerId = borrowerStore.borrower?.id
    const tenantId = affiliateCustomizationStore.tenantId
    const allowedResumeStrategies = ['default', 'resumeSbaApp', 'resumeProgress', 'resumeCompletedProgress', 'resumeLoanAppForced']
    const type = allowedResumeStrategies.includes(resumeAppStrategy) ? resumeAppStrategy : 'default'
    appAnalyticsStore.fsTrackEvent({name: 'Resume Application', properties: { type }})

    if (!borrowerId || !tenantId) {
      throw createError({
        statusCode: 400,
        statusMessage: 'borrowerId and tenantId are required to fetch resume application data',
        fatal: true
      })
    }

    const { data, error } = await applicationConfigService.getResumeApplicationData({ borrowerId, tenantId, type })

    if (error) {
      return _configFetchErrorHandler(error, `resume: ${experienceBaseRoute}`)
    }

    const _applicationConfig = data?.applicationConfig
    const _navigation = data?.navigation
    const _submitSections = data?.submitSections
    const _resumeData = data?.resume

    _setAppExperienceValues({ _applicationConfig, _navigation, _submitSections, _resumeData })
    return _buildResumeUrl()
  }

  async function getApplicationConfigData({entrypointIdentifier = null, moduleIdentifier = null, direction = null} = defaultExperienceArgs) {
    const loadedModuleIdentifier = applicationConfig.value?.module?.identifier

    if (moduleIdentifier && moduleIdentifier === loadedModuleIdentifier && !direction && !stale.value) {
      return applicationConfig.value
    }

    const borrowerId = borrowerStore.borrower?.id
    const tenantId = affiliateCustomizationStore.tenantId
    const anonymousId = rootStore.getAnonymousId()

    if (!borrowerId && !anonymousId) {
      const errorMsg =`Failed to fetch ${entrypointIdentifier ? 'Application Config by Entrypoint' : 'Application Experience' }`
      log.warning(errorMsg, { message: 'No borrowerId or anonymousId', moduleIdentifier })
      return applicationConfig.value
    }

    const payload = {
      anonymousId,
      borrowerId,
      tenantId,
      entrypointIdentifier,
      moduleIdentifier,
      direction,
    }

    const { data, error } = await applicationExperienceService.getApplicationConfigData(payload)

    if (error) {
      const routePrefix = entrypointIdentifier ? `${singleConfigBaseRoute}/${entrypointIdentifier}` : experienceBaseRoute
      return _configFetchErrorHandler(error, `/${routePrefix}/${moduleIdentifier}`)
    }

    const _applicationConfig = data?.applicationConfig
    const _navigation = data?.navigation
    const _submitSections = data?.submitSections

    _setAppExperienceValues({ _applicationConfig, _navigation, _submitSections })
    return applicationConfig.value
  }

  async function getDocumentUploadRequestApplicationConfig(documentUploadRequestInviteToken = null, isContactGuest = false, dealId = null) {
    const _borrowerId = borrowerStore.borrower?.id;
    let url;
    if (_borrowerId) {
      url = dealId
        ? `${env('apiUrl')}/document-upload-request/borrower/${_borrowerId}/deal/${dealId}`
        : `${env('apiUrl')}/document-upload-request/borrower/${_borrowerId}`
    } else {
      url = `${env('apiUrl')}/document-upload-request/${isContactGuest ? 'contact-guest' : 'guest'}`
    }

    const response = await $axios.get(url,
      {
        headers: !_borrowerId && documentUploadRequestInviteToken ? {
          Authorization: `lendio-jwt ${documentUploadRequestInviteToken}`,
        } : null
      }
    )

    const configFromResponse = response?.data?.data ?? null
    documentUploadRequestApplicationConfig.value = configFromResponse
    return documentUploadRequestApplicationConfig.value
  }

  function documentModuleComplete(module) {
    return module.moduleDocuments.every(moduleDocument => moduleDocument.requirements.every(req => req.document))
  }

  async function postApplicationExperienceEvent({ moduleIdentifier = null, eventType, entrypointIdentifier = null, path = null }) {
    if (!eventType) {
      return false
    }
    if (eventType === eventOptions.moduleCompleted && path && !checkPathForCompletedEvent(path)) {
      completedModuleEventPaths.value.push(path)
    }
    const borrowerId = borrowerStore.borrower?.id
    const tenantId = affiliateCustomizationStore.tenantId
    const anonymousId = rootStore.getAnonymousId()

    if (!borrowerId && !anonymousId) {
      log.warning('Failed to save application experience event', { message: 'No borrowerId or anonymousId', eventType })
      return false
    }

    const payload = {
      moduleIdentifier,
      entrypointIdentifier,
      eventType,
      anonymousId,
      borrowerId,
      tenantId,
    }

    const { error } = await applicationConfigService.postApplicationExperienceEvent(payload)

    if (error) {
      log.warning('Failed to save application experience event', { message: error.message, eventType })
    }

    // Return boolean on success. No error = success
    return !error
  }

  function loadAnswersFromStorage() {
    if (borrowerStore.borrower || borrowerStore.borrowerId) {
      return {}
    }
    return applicationConfigService.loadAnswersFromStorage()
  }

  async function saveAnswers({ answers: _answers = null } = {}) {
    if (!Object.keys(_answers).length) {
      return
    }

    if (!borrowerStore.borrower || !borrowerStore.borrowerId) {
      const data = _answers
      applicationConfigService.mergeAnswersToStorage(data)

      return Object.keys(_answers)
    }

    const data = await applicationConfigService.saveAnswers({
      authenticated: Boolean(rootStore.authUser),
      answers: _answers,
      borrowerId: borrowerStore.borrowerId
    })
    stale.value = true

    if (data) {
      return Object.keys(_answers)
    }

    return []
  }

  /**
   * Updates the isDriversLicenseVerificationUiTestEnabled flag based on active experiments
   * Only updates once when the flag becomes true to optimize performance
   * @returns {Promise<void>}
   */
  async function updateDriversLicenseVerificationTestEnabled() {
    if (isDriversLicenseVerificationUiTestEnabled.value === true) return;

    await _updateExperimentEnabledStoreVar({
      experimentState: isDriversLicenseVerificationUiTestEnabled,
      experimentDetails: [{name: 'Drivers License Verification UI Test', allowedAppNames: ['marketplace']}],
    })
  }

  // rootStore.isEmbedded and affiliateCustomizationStore.isIntelligentLendingAffiliate can still
  // be hydrating when this is first evaluated, and there is no libs/app-name-from-route.js value
  // for Intelligent Lending, so the surface is resolved directly from these two flags instead -
  // matching composables/useNonLinearExperiment.js's pattern.
  function _bankPullStatusExperimentName() {
    if (rootStore.isEmbedded) return BANK_PULL_STATUS_EXPERIMENT_NAMES.embedded
    if (affiliateCustomizationStore.isIntelligentLendingAffiliate) return BANK_PULL_STATUS_EXPERIMENT_NAMES.il
    return BANK_PULL_STATUS_EXPERIMENT_NAMES.marketplace
  }

  /**
   * Updates the isBankPullStatusIndicatorEnabled flag based on active experiments.
   * Deliberately re-evaluates on every call rather than latching once true (unlike
   * updateDriversLicenseVerificationTestEnabled): the surface identity this reads from
   * (embedded / Intelligent Lending / marketplace) can still be hydrating on first evaluation.
   * allowedAppNames: ['all'] is required because appNameFromRoute cannot express Intelligent
   * Lending - the surface is already resolved above via rootStore/affiliateCustomizationStore.
   * @returns {Promise<void>}
   */
  async function updateBankPullStatusIndicatorEnabled() {
    await _updateExperimentEnabledStoreVar({
      experimentState: isBankPullStatusIndicatorEnabled,
      experimentDetails: [{name: _bankPullStatusExperimentName(), allowedAppNames: ['all']}],
    })
  }

  /**
   * experimentState is a ref() to track if experiment is enabled
   * experimentDetails contains one more experiments e.g. [{name: 'Experiment Name', allowedAppNames: ['marketplace', 'embedded']}]
   */
  async function _updateExperimentEnabledStoreVar({experimentState, experimentDetails = [], testGroup = 1} = {}) {
    if (!experimentState || experimentState?.value === undefined || !experimentDetails.length) {
      return
    }

    const user = rootStore.authUser
    if (!user) {
      experimentState.value = false
      return
    }

    const appName = appNameFromRoute(route)
    if (!appName) {
      experimentState.value = false
      return
    }

    const filteredExperimentNames = experimentDetails.reduce((returnArray, experiment) => {
      if (experiment.allowedAppNames.includes(appName) || experiment.allowedAppNames.includes('all')) {
        returnArray.push(experiment.name)
      }
      return returnArray
    }, [])
    if (!filteredExperimentNames.length) {
      experimentState.value = false
      return
    }

    await experimentsStore.fetchActiveExperimentsForCurrentUser({ cached: true })
    experimentState.value = experimentsStore.activeUserExperiments
      .some((userExperiment) => filteredExperimentNames.includes(userExperiment.name) && userExperiment.group === testGroup)
  }

  async function _updateAnonymousExperimentEnabledStoreVar({experimentState, experimentDetails = [], testGroup = 1} = {}) {
    if (!experimentState || experimentState?.value === undefined || !experimentDetails.length) {
      return
    }

    const appName = appNameFromRoute(route)
    if (!appName) {
      experimentState.value = false
      return
    }

    const filteredExperimentNames = experimentDetails.reduce((returnArray, experiment) => {
      if (experiment.allowedAppNames.includes(appName)) {
        returnArray.push(experiment.name)
      }
      return returnArray
    }, [])
    if (!filteredExperimentNames.length) {
      experimentState.value = false
      return
    }

    experimentState.value = experimentsStore.anonymousExperiments
      .some((anonExperiment) => {
        return filteredExperimentNames.includes(anonExperiment?.experiment?.name) && anonExperiment.group === testGroup
      })
  }

  function checkPathForCompletedEvent(path = null) {
    if (!path) {
      return false
    }
    return completedModuleEventPaths.value.includes(path)
  }

  function removeCompletedModuleEventPath(path = null) {
    if (!path) {
      return
    }
    const idx = completedModuleEventPaths.value.indexOf(path)
    if (idx !== -1) {
      completedModuleEventPaths.value.splice(idx, 1)
    }
  }

  function setConfigStale(staleValue) {
    stale.value = staleValue
  }

  function _configFetchErrorHandler(error, routeName) {
    let errorStatus
    let errorMsg = `Failed to fetch Application data for ${routeName}`
    switch (error.status) {
      case 403:
        errorStatus = 403
        errorMsg = `${errorMsg} Access denied: ${routeName}`
        break
      case 404:
        errorStatus = 404
        errorMsg = `${errorMsg} Page not found: ${routeName}`
        break
      default:
        errorStatus = 500
        errorMsg = `${errorMsg} ${error.message}`
    }
    throw createError({
      statusCode: errorStatus,
      statusMessage: errorMsg,
      fatal: true
    })
  }

  return {
    // STATE
    applicationConfig,
    completedModuleEventPaths,
    documentUploadRequestApplicationConfig,
    isBackEvent,
    isBankPullStatusIndicatorEnabled,
    isDriversLicenseVerificationUiTestEnabled,
    navigation,
    navItems,
    preloadedIdentifier,
    preloadedPages,
    resumeData,
    resumeUrl,
    stale,
    submitSections,

    // GETTERS
    appIdentifierRoute,
    borrowerApplicationType,
    currentModule,
    currentModuleIdentifier,
    documentUploadModules,

    // ACTIONS
    checkPathForCompletedEvent,
    removeCompletedModuleEventPath,
    getApplicationConfigData,
    getDocumentUploadRequestApplicationConfig,
    loadAnswersFromStorage,
    postApplicationExperienceEvent,
    saveAnswers,
    setConfigStale,
    setIsBackEvent,
    getResumeApplicationData,
    updateBankPullStatusIndicatorEnabled,
    updateDriversLicenseVerificationTestEnabled,
  }
})
